/**
 * Read + mutate `default_checklists` rows for a pipeline.
 *
 * Used by the Manage Columns modal (Phase 2 UI) — list/add/rename/reorder/
 * delete checklist templates for one pipeline (stage). Each row drives the
 * default appearance of a checklist item across every deal that lands in
 * that pipeline.
 *
 * Note: until the user runs `pnpm supabase gen types`, the auto-generated
 * `Database` type does not know about `default_checklists`. We cast at the
 * query level (`supabase.from(...) as unknown as ...`) rather than casting
 * the whole client, so the rest of the file stays type-safe.
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { toUserMessage } from "@/lib/supabase-errors";
import type { DefaultChecklistRow } from "@/types/pipeline";

const TABLE = "default_checklists";

interface InsertInput {
  pipelineId: string;
  checklistText: string;
  itemType?: "check" | "input";
  required?: boolean;
  order?: number;
}

interface UpdateInput {
  id: string;
  patch: Partial<
    Pick<
      DefaultChecklistRow,
      "checklist_text" | "item_type" | "required" | "order"
    >
  >;
}

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

export function useDefaultChecklists(pipelineId: string | null | undefined) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const enabled = Boolean(pipelineId && currentWorkspace?.id);

  const queryKey = ["default_checklists", pipelineId] as const;

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async () => {
      if (!pipelineId) return [] as DefaultChecklistRow[];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (
                col: string,
                v: string,
              ) => { order: (col: string) => DbResp<DefaultChecklistRow[]> };
            };
          };
        }
      )
        .from(TABLE)
        .select("*")
        .eq("pipeline_id", pipelineId)
        .order("order")) as PostgrestSingleResponse<DefaultChecklistRow[]>;

      if (error) throw error;
      return data ?? [];
    },
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({
      queryKey: ["default_checklists_counts"],
    });
  }, [queryClient, queryKey]);

  const addItem = useMutation({
    mutationFn: async (input: InsertInput) => {
      if (!user?.id || !currentWorkspace?.id) {
        throw new Error("Not in a workspace");
      }
      const payload = {
        pipeline_id: input.pipelineId,
        workspace_id: currentWorkspace.id,
        checklist_text: input.checklistText,
        item_type: input.itemType ?? "check",
        required: input.required ?? false,
        order: input.order ?? 0,
        created_by: user.id,
      };
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            insert: (v: unknown) => {
              select: (s: string) => { single: () => DbResp<DefaultChecklistRow> };
            };
          };
        }
      )
        .from(TABLE)
        .insert(payload)
        .select("*")
        .single()) as PostgrestSingleResponse<DefaultChecklistRow>;

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Checklist item added");
    },
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to add checklist item")),
  });

  const updateItem = useMutation({
    mutationFn: async (input: UpdateInput) => {
      const { error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            update: (v: unknown) => {
              eq: (col: string, v: string) => DbResp<DefaultChecklistRow>;
            };
          };
        }
      )
        .from(TABLE)
        .update(input.patch)
        .eq("id", input.id)) as PostgrestSingleResponse<DefaultChecklistRow>;

      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to update checklist item")),
  });

  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            delete: () => {
              eq: (col: string, v: string) => DbResp<unknown>;
            };
          };
        }
      )
        .from(TABLE)
        .delete()
        .eq("id", id)) as PostgrestSingleResponse<unknown>;

      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Checklist item removed");
    },
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to remove checklist item")),
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    addItem,
    updateItem,
    deleteItem,
    refetch: query.refetch,
  };
}

/**
 * Counts of default_checklists rows grouped by pipeline_id for the current
 * workspace. Used by the Manage Columns modal to show a badge on each
 * pipeline row indicating how many checklist templates it has.
 */
export function useDefaultChecklistCounts() {
  const { currentWorkspace } = useWorkspace();

  const query = useQuery({
    queryKey: ["default_checklists_counts", currentWorkspace?.id] as const,
    enabled: Boolean(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id) return {} as Record<string, number>;
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (
                col: string,
                v: string,
              ) => DbResp<Array<{ pipeline_id: string }>>;
            };
          };
        }
      )
        .from(TABLE)
        .select("pipeline_id")
        .eq("workspace_id", currentWorkspace.id)) as PostgrestSingleResponse<
        Array<{ pipeline_id: string }>
      >;
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of data ?? []) {
        counts[row.pipeline_id] = (counts[row.pipeline_id] ?? 0) + 1;
      }
      return counts;
    },
  });

  return {
    counts: query.data ?? {},
    isLoading: query.isLoading,
  };
}
